import { useCallback, useState } from 'react'
import type { HttpTrigger } from '~/prix/actionDefinition'
import useAPI from '~/prix/react/hooks/api'
import { apiBasePath } from '~/utils/shared/platform'
import buildInsightsExportDefinition from '~/packages/insight/export/buildInsightsExport.definition'
import resolveCurrentRequestToken from './resolveCurrentRequestToken'

export interface InsightExportFilters {
  insightIds?: string[]
  search?: string
  delimitation?: string
  variableKeys?: string[]
  variablePresetKeys?: string[]
  alertType?: string[]
  alertPreset?: string
  sortBy?: 'relevance' | 'description' | 'state' | 'alertBadge' | 'alertType' | 'variable'
  sortOrder?: 'asc' | 'desc'
}

export interface ExportResult {
  blob: Blob
  filename: string
}

export default function useInsightExport() {
  const { context } = useAPI()
  const { bypassAuth, getToken, token: contextToken } = context
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const exportInsights = useCallback(
    async (filters: InsightExportFilters, format: 'xlsx' | 'csv'): Promise<ExportResult> => {
      if (isLoading) {
        throw new Error('Export já em andamento')
      }

      setIsLoading(true)
      setError(null)

      const trigger = buildInsightsExportDefinition.triggers.find(
        (value): value is HttpTrigger => value.trigger === 'http',
      )

      if (!trigger) {
        const finalError = new Error('Trigger HTTP não encontrado')
        setError(finalError)
        setIsLoading(false)
        throw finalError
      }

      try {
        const token = await resolveCurrentRequestToken({
          bypassAuth,
          getToken,
          token: contextToken,
        })
        const response = await fetch(`${apiBasePath}${trigger.route}`, {
          method: trigger.method,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(token ? { Token: token } : {}),
          },
          body: JSON.stringify({
            ...filters,
            format,
          }),
        })

        if (!response.ok) {
          const message = await response.text()
          throw new Error(message || 'Erro ao exportar insights')
        }

        const contentDisposition = response.headers.get('Content-Disposition')
        const match = contentDisposition?.match(/filename=\"?([^\";]+)\"?/)
        const filename =
          (match && decodeURIComponent(match[1])) ||
          `insights-${new Date().toISOString().split('T')[0]}.${format}`

        return {
          blob: await response.blob(),
          filename,
        }
      } catch (error) {
        console.error('[useInsightExport] Export error', error)
        const finalError =
          error instanceof Error ? error : new Error('Erro ao exportar insights')
        setError(finalError)
        throw finalError
      } finally {
        setIsLoading(false)
      }
    },
    [bypassAuth, contextToken, getToken, isLoading],
  )

  return {
    exportInsights,
    isLoading,
    error,
  }
}
